import React, { useCallback, useMemo } from 'react';

import { useCustomer } from '@order/Customer';

import type {
  BillingAccount,
  Customer,
  SubmitBraintreeBillingAccountSuccessResponse,
  UserError,
} from '../../../graphql/types';
import { getUrqlError } from '../../../utils/common';
import type { CreditCardForm as Form } from '../CreditCardForm.types';
import type { SubmitBraintreeBillingAccountMutation } from '../GraphQL/CreditCardForm.generated';
import {
  useDeleteBillingAccountMutation,
  useSubmitBraintreeBillingAccountMutation,
  useUpdateBillingAccountMutation,
} from '../GraphQL/CreditCardForm.generated';
import { useCreditCardErrorHandling } from './CreditCardForm.hooks.errorHandling';

export const useCreditCardHandlers = ({
  paymentMethodId,
  onComplete,
}: CreditCardHandlersProps = {}) => {
  const notifyError = useCreditCardErrorHandling();

  // ────────────────────────────── State ───────────────────────────────────────
  const { customer, setCustomerData } = useCustomer();
  const {
    submitBillingAccountResponse,
    submitBillingAccount,
    parseBillingAccountResponse,
  } = useSubmitBillingAccount();
  const [updateBillingAccountResponse, updateBillingAccount] =
    useUpdateBillingAccountMutation();
  const [deleteBillingAccountResponse, deleteBillingAccount] =
    useDeleteBillingAccountMutation();

  // ───────────────────────────- Callbacks ────────────────────────────────────

  const handleOnSave = useCallback(
    (form: Form, nonce: string) => {
      (async () => {
        if (!nonce) return;
        const response = await submitBillingAccount({
          input: {
            nonce,
            nickname: form.nickname,
            isDefault: form.default,
          },
        });

        // Feature flagged billing account submission response.
        const { typename, billingAccount, errorMessage, isKnownUserError } =
          parseBillingAccountResponse(response.data);

        // Successfully added billing account.
        if (billingAccount?.id) {
          setCustomerData({
            ...customer,
            billingAccounts: getUpdatedBillingAccounts({
              customer,
              form,
              billingAccount,
            }),
          });
          onComplete?.();

          return;
        }

        // Handling known user errors.
        if (isKnownUserError) {
          notifyError({
            typename,
            errorMessage,
            systemError: errorMessage,
          });

          return;
        }

        // Handling unknown user error.
        if (errorMessage) {
          notifyError({ errorMessage, systemError: typename });

          return;
        }

        // Handling system error.
        if (response?.error) {
          notifyError({
            systemError: getUrqlError(response?.error),
          });
        }
      })();
    },
    [
      customer,
      submitBillingAccount,
      parseBillingAccountResponse,
      notifyError,
      setCustomerData,
      onComplete,
    ],
  );

  const handleOnEdit = useCallback(
    (form: Form) => {
      (async () => {
        if (!paymentMethodId) return;
        const response = await updateBillingAccount({
          input: {
            id: Number(paymentMethodId),
            nickname: form.nickname,
            isDefault: form.default,
          },
        });
        const billingAccount =
          response.data?.updateBillingAccount?.billingAccount;

        // Successfully edited billing account.
        if (billingAccount?.id) {
          setCustomerData({
            ...customer,
            billingAccounts: getUpdatedBillingAccounts({
              customer,
              form,
              billingAccount,
            }),
          });
          onComplete?.();
        }

        // Handling system error.
        if (response?.error) {
          notifyError({
            systemError: getUrqlError(response?.error),
          });
        }
      })();
    },
    [
      paymentMethodId,
      customer,
      updateBillingAccount,
      notifyError,
      setCustomerData,
      onComplete,
    ],
  );

  const handleOnDelete = useCallback(() => {
    (async () => {
      if (!paymentMethodId) return;
      const response = await deleteBillingAccount({
        input: {
          id: Number(paymentMethodId),
        },
      });

      // Successfully deleted billing account.
      if (response?.data?.deleteBillingAccount?.success) {
        const billingAccounts = (customer?.billingAccounts ?? [])?.filter(
          ({ id }) => id !== paymentMethodId,
        );

        setCustomerData({
          ...customer,
          billingAccounts,
        });
      }

      // Handling system error.
      if (response?.error) {
        notifyError({
          isDeleteRequest: true,
          systemError: getUrqlError(response?.error),
        });
      }
    })();
  }, [
    customer,
    paymentMethodId,
    deleteBillingAccount,
    notifyError,
    setCustomerData,
  ]);

  return React.useMemo(
    () => ({
      customer,
      submitBillingAccountResponse,
      updateBillingAccountResponse,
      deleteBillingAccountResponse,
      handleOnSave,
      handleOnEdit,
      handleOnDelete,
      parseBillingAccountResponse,
    }),
    [
      customer,
      submitBillingAccountResponse,
      updateBillingAccountResponse,
      deleteBillingAccountResponse,
      handleOnSave,
      handleOnEdit,
      handleOnDelete,
      parseBillingAccountResponse,
    ],
  );
};

// Returns a new array with the billing accounts in the correct "isDefault" state.
const getUpdatedBillingAccounts = (props: BillingAccountFormatterProps) => {
  const billingAccounts = (props.customer?.billingAccounts ?? [])?.filter(
    ({ id }) => id !== props.billingAccount?.id,
  );
  const nonDefaultedAccounts = props.form.default
    ? billingAccounts.map((account) => ({
        ...account,
        isDefault: false,
      }))
    : billingAccounts;

  return [...nonDefaultedAccounts, props.billingAccount];
};

export const useSubmitBillingAccount = () => {
  const [submitBillingAccountResponse, submitBillingAccount] =
    useSubmitBraintreeBillingAccountMutation();

  // Parsing mutation response.
  const parseBillingAccountResponse = useCallback(
    (response?: SubmitBraintreeBillingAccountMutation) => {
      // Data and typename.
      const data = response?.submitBraintreeBillingAccount;
      const typename = data?.__typename ?? '';

      // Billing account.
      const billingAccount = (
        data as SubmitBraintreeBillingAccountSuccessResponse
      )?.billingAccount;

      // Error message.
      const errorMessage = (data as unknown as UserError)?.errorMessage;

      // User errors we currently expect.
      const isKnownUserError = [
        'InvalidSubmittedBillingAccount',
        'InvalidPostalCode',
        'TooManyRequests',
      ].includes(typename);

      return {
        typename,
        billingAccount,
        errorMessage,
        isKnownUserError,
      };
    },
    [],
  );

  return useMemo(
    () => ({
      submitBillingAccountResponse,
      submitBillingAccount,
      parseBillingAccountResponse,
    }),
    [
      submitBillingAccountResponse,
      submitBillingAccount,
      parseBillingAccountResponse,
    ],
  );
};

type BillingAccountFormatterProps = {
  readonly form: Form;
  readonly customer: Partial<Customer>;
  readonly billingAccount: BillingAccount;
};

type CreditCardHandlersProps = {
  readonly paymentMethodId?: string;
  readonly onComplete?: () => void;
};
